import { useQuery } from "@tanstack/react-query";
import { addons } from "@fm/api/addons";
import type { ApiClient } from "@fm/api/types";
import type { Addon } from "@fm/api/addons";
import { getApiBaseUrl } from "../lib/config";

export interface UseVehicleAddonsOptions {
  apiKey: string;
  fleetId: string;
  vehicleId?: string;
  apiBaseUrl?: string;
  enabled?: boolean;
}

export interface UseCategoryAddonsOptions {
  apiKey: string;
  fleetId: string;
  categoryId?: string;
  apiBaseUrl?: string;
  enabled?: boolean;
}

/**
 * Guests are never offered a hidden add-on. The API already leaves it out of
 * the API-key endpoints, so this is a UX safeguard, not the enforcement. An
 * older server omits the field, which reads as visible.
 */
function guestVisible(list: Addon[]): Addon[] {
  return list.filter((addon) => !addon.hidden);
}

/**
 * Hook to fetch addons for a specific vehicle
 *
 * @param options - Hook configuration options
 * @returns Query result with vehicle addons
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useVehicleAddons({
 *   apiKey: 'pk_...',
 *   fleetId: 'fleet-123',
 *   vehicleId: 'vehicle-456'
 * });
 * ```
 */
export function useVehicleAddons(options: UseVehicleAddonsOptions) {
  const { apiKey, fleetId, vehicleId, apiBaseUrl, enabled = true } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useQuery<Addon[], Error>({
    queryKey: ["vehicle-addons", fleetId, vehicleId],
    queryFn: async () => {
      if (!vehicleId) {
        throw new Error("Vehicle ID is required");
      }
      const response = await addons.listVehicleAddons(
        apiClient,
        fleetId,
        vehicleId,
      );
      return guestVisible(response.data);
    },
    enabled: enabled && !!vehicleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch addons for a specific category
 *
 * @param options - Hook configuration options
 * @returns Query result with category addons
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useCategoryAddons({
 *   apiKey: 'pk_...',
 *   fleetId: 'fleet-123',
 *   categoryId: 'category-456'
 * });
 * ```
 */
export function useCategoryAddons(options: UseCategoryAddonsOptions) {
  const { apiKey, fleetId, categoryId, apiBaseUrl, enabled = true } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useQuery<Addon[], Error>({
    queryKey: ["category-addons", fleetId, categoryId],
    queryFn: async () => {
      if (!categoryId) {
        throw new Error("Category ID is required");
      }
      const response = await addons.listCategoryAddons(
        apiClient,
        fleetId,
        categoryId,
      );
      return guestVisible(response.data);
    },
    enabled: enabled && !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
